/**
 * LOD0 Brand Image frontend API client.
 *
 * Reuses the EXACT Territory Identity management authority: SIWE session cookie
 * (`credentials: "include"`) + memory-only session-bound CSRF token (shared with
 * territory-identity-api so a wallet switch clears it once) + trusted Origin
 * (browser-attached). Writes are raw-binary PUT / DELETE carrying the expected
 * brand-image revision for optimistic concurrency. Reads are public.
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken, TerritoryIdentityError } from "./territory-identity-api";

export { clearCsrfToken };

export interface BrandImageMeta {
  identity_id: string;
  mime_type: string;
  byte_size: number;
  width: number;
  height: number;
  sha256: string;
  revision: number;
  updated_at: string | null;
  content_url: string;
}

const EXPECTED_REV_HEADER = "X-Expected-Brand-Image-Revision";

/** Public content URL with a revision cache-buster so a replacement is never
 * masked by a stale cached body (ETag also changes with revision). */
export function brandImageContentUrl(identityId: string, revision: number): string {
  return `${API_BASE}/api/v1/territories/${encodeURIComponent(identityId)}/brand-image/content?v=${revision}`;
}

/** Shape of the backend error envelope (top-level or FastAPI `detail`). */
interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse(resp: Response): Promise<unknown> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new TerritoryIdentityError(resp.status, code, message);
  }
  return body;
}

export async function getBrandImageMeta(
  identityId: string,
  signal?: AbortSignal,
): Promise<BrandImageMeta | null> {
  const resp = await fetch(
    `${API_BASE}/api/v1/territories/${encodeURIComponent(identityId)}/brand-image`,
    { credentials: "include", headers: { Accept: "application/json" }, signal },
  );
  if (resp.status === 404) return null;
  const body = (await parse(resp)) as { brand_image: BrandImageMeta };
  return body.brand_image ?? null;
}

/** Send a CSRF-protected mutation, refetching the token ONCE on a stale-token
 * 403 (mirrors territory-identity-api.csrfPost, generalised for any method/body). */
async function csrfSend(
  path: string,
  method: "PUT" | "DELETE",
  extraHeaders: Record<string, string>,
  body?: BodyInit,
  signal?: AbortSignal,
): Promise<Response> {
  const doSend = (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers: { "X-The-Grid-CSRF": csrf, ...extraHeaders },
      signal,
      body,
    });
  let resp = await doSend(await getCsrfToken());
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => null);
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doSend(await getCsrfToken(true));
    }
  }
  return resp;
}

export async function putBrandImage(
  identityId: string,
  bytes: ArrayBuffer | Blob,
  contentType: string,
  expectedRevision: number,
  signal?: AbortSignal,
): Promise<{ brand_image: BrandImageMeta }> {
  const resp = await csrfSend(
    `/api/v1/territories/${encodeURIComponent(identityId)}/brand-image`,
    "PUT",
    { "Content-Type": contentType, [EXPECTED_REV_HEADER]: String(expectedRevision) },
    bytes,
    signal,
  );
  return (await parse(resp)) as { brand_image: BrandImageMeta };
}

export async function deleteBrandImage(
  identityId: string,
  expectedRevision: number,
  signal?: AbortSignal,
): Promise<void> {
  const resp = await csrfSend(
    `/api/v1/territories/${encodeURIComponent(identityId)}/brand-image`,
    "DELETE",
    { [EXPECTED_REV_HEADER]: String(expectedRevision) },
    undefined,
    signal,
  );
  await parse(resp);
}

export async function batchBrandImages(
  identityIds: string[],
  signal?: AbortSignal,
): Promise<BrandImageMeta[]> {
  const unique = Array.from(new Set(identityIds.filter(Boolean)));
  if (unique.length === 0) return [];
  const resp = await fetch(
    `${API_BASE}/api/v1/brand-images?identity_ids=${encodeURIComponent(unique.join(","))}`,
    { credentials: "include", headers: { Accept: "application/json" }, signal },
  );
  const body = (await parse(resp)) as { brand_images: BrandImageMeta[] };
  return body.brand_images ?? [];
}

/** Public viewer discovery: persistent identity anchored to a loaded parcel. */
export interface DiscoveredIdentityDTO {
  identity_id: string;
  owner: string;
  display_name: string | null;
  member_parcel_ids: number[];
}

/**
 * Map a bounded set of LOADED parcel ids to the ACTIVE persistent Territory
 * Identities whose binding intersects them (ONE backend `$in` query, no N+1).
 */
export async function batchTerritoryIdentities(
  parcelIds: number[],
  signal?: AbortSignal,
): Promise<DiscoveredIdentityDTO[]> {
  const unique = Array.from(new Set(parcelIds.filter((n) => Number.isFinite(n))));
  if (unique.length === 0) return [];
  const resp = await fetch(
    `${API_BASE}/api/v1/grid/territory-identities?parcel_ids=${encodeURIComponent(unique.join(","))}`,
    { credentials: "include", headers: { Accept: "application/json" }, signal },
  );
  const body = (await parse(resp)) as { identities: DiscoveredIdentityDTO[] };
  return body.identities ?? [];
}
